/* M Spa 2026 calendar. Dates are explicit ISO instants, including the DST boundary.
 * This component controls presentation and cart forms, not checkout eligibility.
 * Product/discount scheduling and patient limits must be configured before ready=true.
 */
const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
const normalize = (value) => String(value).trim().toLowerCase().replace(/\s+/g, ' ');
const icons = {
  lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="6" y="10" width="12" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>',
  arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>',
};

export function offerState(offer, now = Date.now()) {
  const start = Date.parse(offer.opensAt);
  const end = Date.parse(offer.closesAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 'locked';
  if (now < start) return 'locked';
  return now < end ? 'available' : 'expired';
}

export function previewInstant(value, offers, now = Date.now()) {
  if (value === 'before') return Date.parse(offers[0].opensAt) - 86400000;
  if (value === 'after') return Date.parse(offers.at(-1).closesAt) + 1000;
  if (value === 'overlap') return Date.parse(offers[1].opensAt) + 3600000;
  const offer = offers.find((item) => String(item.day) === String(value));
  // Noon Pacific on the offer's named date, including the first day's DST change.
  return offer ? Date.parse(offer.closesAt) - 43200000 : now;
}

export function countdown(milliseconds) {
  let seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${days ? `${days}d ` : ''}${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds % 60).padStart(2, '0')}s`;
}

export function purchaseTarget(offer, optionIndex, mapping, preview, now) {
  const option = offer.options[optionIndex];
  const label = offer.kind === 'external_membership' ? `Enroll in ${option?.label || 'membership'}` : 'Add to cart';
  const unavailable = { label, message: preview ? '' : 'Please check back for online availability.' };
  const state = offerState(offer, now);
  if (state !== 'available') return { label: state === 'locked' ? 'Coming soon' : 'Offer ended', message: preview ? '' : 'This gift is not currently available at its promotional price.' };
  if (!mapping?.ready || !option) return unavailable;
  if (offer.kind === 'external_membership') {
    if (!/^https:\/\/mspa\.repeatmd\.app\/pdp\/membership\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(option.enrollmentUrl || '')) return unavailable;
    return { label, href: option.enrollmentUrl, message: '' };
  }
  const separateProducts = offer.day === 1 || offer.kind === 'multiple_products';
  const product = mapping.products?.[separateProducts ? optionIndex : 0];
  if (!product) return unavailable;
  const variants = product.variants || [];
  const variant = (separateProducts || offer.options.length === 1) && variants.length === 1
    ? variants[0]
    : variants.find((item) => normalize(item.title) === normalize(option.label));
  // Never link a planned offer to an old price or guess a variant from its position.
  if (!variant || Number(variant.price) !== Math.round(option.price_usd * 100)) return unavailable;
  if (!variant.available) return { label: 'Sold out', message: 'This selection is currently sold out.' };
  if (!/^(?:\/[a-z]{2}(?:-[A-Z]{2})?)?\/products\/[a-z0-9-]+\/?$/.test(product.url) || !/^\d+$/.test(variant.id)) return unavailable;
  return { label, variantId: variant.id, message: '' };
}

const dateLabel = (iso, includeTime = false) => new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
}).format(new Date(iso));

class MspaAdvent extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.initialize(this.abort.signal);
  }

  async initialize(signal) {
    try {
      this.settings = JSON.parse(this.querySelector('[data-settings]').textContent);
      const embedded = this.querySelector('[data-manifest-inline]');
      let data;
      if (embedded) data = JSON.parse(embedded.textContent);
      else {
        const response = await fetch(this.dataset.manifest, { signal });
        if (!response.ok) throw new Error('Calendar unavailable');
        data = await response.json();
      }
      if (signal.aborted || !this.isConnected) return;
      if (!Array.isArray(data.offers) || data.offers.length !== 12) throw new Error('Incomplete calendar');
      this.offers = data.offers;
      this.stateSignature = '';
      this.grid = this.querySelector('[data-grid]');
      this.clock = this.querySelector('[data-clock]');
      this.announcement = this.querySelector('[data-announcement]');
      this.renderGrid();
      this.createDialog(signal);
      this.grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-open]');
        if (!button) return;
        const offer = this.offers.find((item) => String(item.day) === button.dataset.open);
        if (offerState(offer, this.now()) === 'locked') {
          this.announcement.textContent = `Day ${offer.day} opens ${dateLabel(offer.opensAt, true)} Pacific.`;
          return;
        }
        this.openOffer(offer, button);
      }, { signal });
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.update(); }, { signal });
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.querySelector('[data-load-error]').hidden = false;
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearInterval(this.timer);
    if (this.dialog?.open) this.dialog.close();
    this.restoreScroll();
    this.dialog?.remove();
  }

  now() {
    return Date.now();
  }

  renderGrid() {
    this.grid.innerHTML = this.offers.map((offer) => `<article class="mspa-advent__gift" data-day="${offer.day}">
      <button class="mspa-advent__box" type="button" data-open="${offer.day}" aria-haspopup="dialog">
        <span class="mspa-advent__box-date">November ${offer.day}</span>
        <span class="mspa-advent__number" aria-hidden="true">${offer.day}</span>
        <span class="mspa-advent__box-action" data-action></span>
      </button>
      <div class="mspa-advent__caption" data-caption></div>
    </article>`).join('');
  }

  update() {
    if (!this.isConnected || !this.offers) return;
    const now = this.now();
    const states = this.offers.map((offer) => offerState(offer, now));
    const signature = states.join(',');
    if (signature !== this.stateSignature) {
      this.stateSignature = signature;
      this.offers.forEach((offer, index) => {
        const state = states[index];
        const card = this.grid.querySelector(`[data-day="${offer.day}"]`);
        const button = card.querySelector('button');
        card.dataset.state = state;
        button.setAttribute('aria-disabled', String(state === 'locked'));
        button.setAttribute('aria-label', state === 'locked' ? `Day ${offer.day}. Opens ${dateLabel(offer.opensAt, true)} Pacific.` : `Day ${offer.day}: ${offer.title}. ${state === 'available' ? 'Open gift' : 'Offer ended. View details'}.`);
        card.querySelector('[data-action]').innerHTML = state === 'locked' ? `${icons.lock} Coming soon` : state === 'available' ? `Open your gift ${icons.arrow}` : 'Offer ended';
        card.querySelector('[data-caption]').innerHTML = state === 'locked' ? `Opens ${escapeHtml(dateLabel(offer.opensAt))} · 8 p.m. PT` : state === 'available' ? `<strong>Open now</strong>Until ${dateLabel(Date.parse(offer.closesAt) - 1)} · 11:59 p.m. PT` : `<strong>${escapeHtml(offer.title)}</strong>`;
      });
      const count = states.filter((state) => state === 'available').length;
      this.announcement.textContent = count ? `${count} ${count === 1 ? 'gift is' : 'gifts are'} open now.` : states.every((state) => state === 'expired') ? 'The 2026 offers have ended.' : 'The calendar is ready. The first gift opens October 31 at 8 p.m. Pacific.';
    }
    const available = this.offers.filter((_, index) => states[index] === 'available');
    const next = this.offers.find((_, index) => states[index] === 'locked');
    if (next) {
      this.clock.innerHTML = `<strong>${available.length ? `${available.length} ${available.length === 1 ? 'gift' : 'gifts'} open now` : 'Your first gift is almost here'}</strong><span>${available.length ? 'Next gift' : 'Opens'} in <time>${countdown(Date.parse(next.opensAt) - now)}</time></span>`;
    } else if (available.length) {
      const close = Math.min(...available.map((offer) => Date.parse(offer.closesAt)));
      this.clock.innerHTML = `<strong>${available.length === 1 ? 'Your final gift is open' : 'Two gifts are open'}</strong><span>${available.length === 1 ? 'Closes' : 'Next offer closes'} in <time>${countdown(close - now)}</time></span>`;
    } else {
      this.clock.innerHTML = '<strong>All twelve gifts have been opened.</strong><span>The 2026 offers have ended. Thank you for celebrating with M Spa.</span>';
    }
    if (this.dialog?.open) this.updatePurchase();
  }

  createDialog(signal) {
    this.dialog?.remove();
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'mspa-advent__dialog';
    this.dialogId = `advent-offer-${++MspaAdvent.instances}`;
    this.dialog.setAttribute('aria-labelledby', `${this.dialogId}-title`);
    this.append(this.dialog);
    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]') || event.target === this.dialog) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.restoreScroll();
      if (this.trigger?.isConnected) this.trigger.focus();
    }, { signal });
    this.dialog.addEventListener('change', (event) => {
      if (event.target.matches('[data-options]')) {
        this.optionIndex = Number(event.target.value);
        this.renderVisual();
        this.renderPrice();
        this.updatePurchase();
      }
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (!event.target.closest('a[data-cta]')) return;
      // Re-check on activation so a tab left open cannot follow an expired offer.
      const target = this.currentTarget();
      if (!target.href) event.preventDefault();
      this.updatePurchase();
    }, { signal });
    this.dialog.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-cart-form]')) return;
      // Refresh the variant and eligibility at submit time, including across expiry.
      const target = this.currentTarget();
      if (!target.variantId) event.preventDefault();
      this.updatePurchase();
    }, { signal });
  }

  restoreScroll() {
    if (this.previousOverflow !== undefined) {
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  openOffer(offer, trigger) {
    this.selected = offer;
    this.optionIndex = 0;
    this.trigger = trigger;
    const label = offer.kind === 'external_membership' ? 'Choose your membership' : offer.day === 5 ? 'Choose your vials' : offer.kind === 'multiple_products' ? 'Choose a product' : offer.kind === 'treatment_credit' ? 'Choose your credit' : offer.day === 1 ? 'Choose your Intellishade' : 'Choose your treatment';
    const limit = offer.limitScope === 'vials' ? `Up to ${offer.limit} vials per patient.` : offer.kind === 'multiple_products' ? 'Limit 1 of each product per patient.' : offer.limitScope === 'per option' ? `Limit ${offer.limit} of each option per patient.` : `Limit ${offer.limit} per patient.`;
    this.dialog.innerHTML = `<button class="mspa-advent__close" type="button" aria-label="Close offer" data-close>${icons.close}</button>
      <div class="mspa-advent__detail">
        <div class="mspa-advent__visual" data-visual></div>
        <div class="mspa-advent__body">
          <h2 id="${this.dialogId}-title" tabindex="-1">${escapeHtml(offer.title)}</h2>
          <p class="mspa-advent__window">Day ${offer.day} · November ${offer.day}<br><span data-window></span></p>
          <p class="mspa-advent__description">${escapeHtml(offer.summary)}</p>
          ${offer.options.length > 1 ? `<label class="mspa-advent__option-label" for="${this.dialogId}-option">${label}</label><select class="mspa-advent__options" id="${this.dialogId}-option" data-options>${offer.options.map((option, index) => `<option value="${index}">${escapeHtml(option.label)} — ${money(option.price_usd)}${offer.kind === 'treatment_credit' ? ` for ${money(option.total_credit_usd)} credit` : ''}</option>`).join('')}</select>` : ''}
          <div data-price aria-live="polite" aria-atomic="true"></div>
          <p class="mspa-advent__limit">${limit}</p>
          ${offer.notes.length ? `<ul class="mspa-advent__notes">${offer.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>` : ''}
          ${offer.kind === 'external_membership' ? '<a class="mspa-advent__cta" data-cta role="link" aria-disabled="true" tabindex="0">Enroll in membership</a>' : `<form action="${escapeHtml(this.settings.cartAddUrl || '/cart/add')}" method="post" data-cart-form><input type="hidden" name="id" value="" data-cart-variant><input type="hidden" name="quantity" value="1"><button class="mspa-advent__cta" type="submit" data-cta disabled aria-disabled="true">Add to cart</button></form>`}
          <p class="mspa-advent__purchase-note" data-purchase-note hidden></p>
        </div>
      </div>`;
    this.renderVisual();
    this.renderPrice();
    this.updatePurchase();
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    this.dialog.querySelector('h2').focus({ preventScroll: true });
  }

  renderVisual() {
    const offer = this.selected;
    const images = (offer.imagesByOption?.[this.optionIndex] || offer.images).filter((image) => /^https:\/\/cdn\.shopify\.com\//.test(image.src));
    const visual = this.dialog.querySelector('[data-visual]');
    visual.classList.toggle('mspa-advent__visual--bundle', images.length > 1);
    const caption = offer.kind === 'multiple_products' ? offer.options[this.optionIndex].label : offer.kind === 'treatment_credit' ? 'A little more glow.' : 'The gift that keeps giving.';
    visual.innerHTML = images.length ? images.map((image) => `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" width="420" height="420" loading="eager" decoding="async">`).join('') : `<div class="mspa-advent__visual-number">${offer.day}</div><div class="mspa-advent__visual-caption">${escapeHtml(caption)}</div>`;
  }

  renderPrice() {
    const offer = this.selected;
    const option = offer.options[this.optionIndex];
    const credit = offer.kind === 'treatment_credit';
    const membership = offer.kind === 'external_membership';
    this.dialog.querySelector('[data-price]').innerHTML = `<div class="mspa-advent__price"><strong>${money(option.price_usd)}</strong>${option.regular_price_usd ? `<del><span class="mspa-advent__sr">Regular price </span>${money(option.regular_price_usd)}</del>` : ''}${membership ? '<span>first month</span>' : ''}</div>
      <p class="mspa-advent__savings">${credit ? `${money(option.total_credit_usd)} treatment credit · includes ${money(option.bonus_credit_usd)} bonus` : membership ? `${money(option.regular_monthly_price_usd)}/month from month two · ${option.product_and_service_discount_percent}% off products and services` : `Save ${money(option.regular_price_usd - option.price_usd)}${offer.day === 5 ? ` · ${money(775)} per vial` : ''}`}</p>`;
  }

  currentTarget() {
    const mappings = (this.settings.mappings || []).filter((item) => Number(item.day) === this.selected.day);
    // A duplicated day in the Theme Editor fails closed instead of choosing silently.
    return purchaseTarget(this.selected, this.optionIndex, mappings.length === 1 ? mappings[0] : null, this.settings.preview, this.now());
  }

  updatePurchase() {
    if (!this.selected) return;
    const state = offerState(this.selected, this.now());
    const windowLabel = state === 'available' ? `Open until ${dateLabel(Date.parse(this.selected.closesAt) - 1)}, 11:59 p.m. Pacific` : 'This offer has ended';
    this.dialog.querySelector('[data-window]').textContent = windowLabel;
    const target = this.currentTarget();
    const link = this.dialog.querySelector('[data-cta]');
    link.textContent = target.label;
    if (this.selected.kind === 'external_membership') {
      link.setAttribute('aria-disabled', String(!target.href));
      if (target.href) link.href = target.href;
      else link.removeAttribute('href');
    } else {
      link.disabled = !target.variantId;
      link.setAttribute('aria-disabled', String(link.disabled));
      this.dialog.querySelector('[data-cart-variant]').value = target.variantId || '';
    }
    const note = this.dialog.querySelector('[data-purchase-note]');
    note.textContent = target.message;
    note.hidden = !target.message;
  }
}
if (globalThis.customElements && !customElements.get('mspa-advent')) customElements.define('mspa-advent', MspaAdvent);
